import React, { useMemo } from 'react';
import { BALL_COLORS, GOLD, shownValue } from './raffleTokens';

/**
 * The draw animation. Presentation only (R64): the number it lands on was
 * generated and saved by the server before this ever started spinning.
 *
 * phase: idle | spin | result. `tick` is the number flashing during the
 * spin; `value` is the server's result.
 */
export default function DrawStage({ phase, value, tick, mode, accent }) {
  const balls = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => {
        const a = (i / 16) * Math.PI * 2;
        const r = 26 + (i % 4) * 8;
        return {
          n: mode === 'machine' ? ((i * 7) % 64) + 1 : ((i * 37) % 90) + 10,
          x: 50 + Math.cos(a) * r,
          y: 50 + Math.sin(a) * r,
          c: mode === 'machine' ? BALL_COLORS[i % BALL_COLORS.length] : accent,
          s: 15 + (i % 3) * 5,
          d: (i % 6) * 0.13,
        };
      }),
    [mode, accent]
  );

  const burst = useMemo(
    () =>
      Array.from({ length: 46 }, (_, i) => {
        const a = (i / 46) * Math.PI * 2 + (i % 3) * 0.22;
        const dist = 110 + (i % 7) * 26;
        return {
          dx: Math.cos(a) * dist,
          dy: Math.sin(a) * dist,
          c: BALL_COLORS[i % BALL_COLORS.length],
          w: i % 4 === 0 ? 5 : 4 + (i % 3) * 3,
          h: i % 4 === 0 ? 5 : 10 + (i % 4) * 5,
          round: i % 4 === 0,
          delay: (i % 5) * 0.045,
          dur: 0.75 + (i % 6) * 0.11,
          rot: (i % 2 ? 1 : -1) * (180 + (i % 5) * 90),
        };
      }),
    []
  );

  // Ticket and auto numbers run to four or five digits, so they get the
  // roomier readout; a machine number never does.
  const big = mode !== 'machine';
  const label =
    phase === 'spin'
      ? 'Drawing…'
      : phase === 'result'
      ? { machine: 'Winning machine', ticket: 'Winning ticket' }[mode] ||
        'Winning number'
      : 'Ready to draw';

  return (
    <div className="rf-stage">
      <div className="rf-stage-dots" />
      <div className="rf-stage-inner rf-anim">
        <div
          className="rf-stage-label"
          style={{ color: phase === 'result' ? '#43e08d' : GOLD }}
        >
          {label}
        </div>

        <div className="rf-wheel-wrap">
          {phase === 'result' && (
            <div className="rf-burst-origin">
              {[0, 0.85, 1.7].map((wave, w) => (
                <React.Fragment key={w}>
                  <div
                    className="rf-flash"
                    style={{
                      animation: `rfFlash 500ms ease-out ${wave}s forwards`,
                    }}
                  />
                  {[0, 0.16].map((d, i) => (
                    <div
                      key={i}
                      className="rf-shock"
                      style={{
                        border: `6px solid ${i ? '#43e08d' : GOLD}`,
                        animation: `rfShock 900ms cubic-bezier(.15,.7,.3,1) ${
                          wave + d
                        }s forwards`,
                      }}
                    />
                  ))}
                  {burst.map((p, i) => (
                    <div
                      key={i}
                      className="rf-particle"
                      style={{
                        width: p.w,
                        height: p.h,
                        background: p.c,
                        borderRadius: p.round ? '50%' : 2,
                        '--dx': `${
                          p.dx * (w === 1 ? 0.82 : w === 2 ? 1.12 : 1)
                        }px`,
                        '--dy': `${
                          p.dy * (w === 1 ? 1.1 : w === 2 ? 0.86 : 1)
                        }px`,
                        '--rot': `${p.rot * (w === 1 ? -1 : 1)}deg`,
                        animation: `rfSplash ${
                          p.dur
                        }s cubic-bezier(.1,.75,.3,1) ${
                          wave + p.delay
                        }s forwards`,
                      }}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}

          <div
            className="rf-wheel"
            style={{
              border: `3px solid ${
                phase === 'result' ? GOLD : 'rgba(255,255,255,0.28)'
              }`,
              animation:
                phase === 'spin' ? 'rfWheel 1.4s linear infinite' : 'none',
              opacity: phase === 'idle' ? 0.55 : 1,
            }}
          />

          <div
            className="rf-globe"
            style={{
              animation:
                phase === 'result'
                  ? 'rfGlow 1.6s ease-in-out infinite'
                  : 'none',
            }}
          >
            <div
              className="rf-balls"
              style={{
                animation:
                  phase === 'spin'
                    ? 'rfWheelBack 0.85s linear infinite'
                    : 'none',
                opacity: phase === 'result' ? 0.18 : phase === 'idle' ? 0.5 : 1,
              }}
            >
              {balls.map((b, i) => (
                <div
                  key={i}
                  className="rf-ball"
                  style={{
                    left: `${b.x}%`,
                    top: `${b.y}%`,
                    width: b.s,
                    height: b.s,
                    marginLeft: -b.s / 2,
                    marginTop: -b.s / 2,
                    background: b.c,
                    fontSize: b.s * 0.42,
                    animation:
                      phase === 'spin'
                        ? `rfBob 0.5s ease-in-out ${b.d}s infinite`
                        : 'none',
                  }}
                >
                  {b.n}
                </div>
              ))}
            </div>

            <div className="rf-globe-center">
              {phase === 'spin' && (
                <span className="rf-tick" style={{ fontSize: big ? 46 : 60 }}>
                  {shownValue(mode, tick)}
                </span>
              )}
              {phase === 'result' && (
                <span className="rf-result" style={{ fontSize: big ? 62 : 84 }}>
                  {shownValue(mode, value)}
                </span>
              )}
              {phase === 'idle' && <span className="rf-idle-dash">--</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
